import update from 'immutability-helper';
import * as types from '../actions/actionTypes';
import initialState from './initialState';
import { buildFolderStructure, organiseFolderData, sortByName } from "../utils/organiseFolders";

export default function folderReducer(state = initialState.folders, action) {
  const content = { ...state };

  switch (action.type) {
    case types.LOAD_MY_FOLDERS_SUCCESS:
      content.data = action.folders;
      content.loading = false;
      return content;

    case types.LOAD_MY_FOLDERS_V2_SUCCESS:
      const folderData = organiseFolderData(action.folders);
      content.data = folderData.data;
      content.idTree = folderData.idTree;
      content.loading = false;
      return content;

    case types.LOAD_FOLDER_V3_SUCCESS:
      // set folder.info to 'full' now we have the information
      let allData = content.data.concat(action.contents);
      if (action.folderIds) {
        // loading full data for specific folders
        allData = allData.map((item) => {
          if (item.type === `folder` && action.folderIds.includes(item.id)) {
            return update(item, { info: { $set: `full` } });
          }
          return item;
        });
      }

      const folderInfo = organiseFolderData(allData);
      content.data = folderInfo.data;
      content.idTree = folderInfo.idTree;
      content.loading = false;
      return content;

    case types.GET_MY_FOLDERS_SUCCESS:
      const folderStructure = buildFolderStructure(action.folders);
      content.data = folderStructure.data;
      content.idTree = folderStructure.idTree;
      content.loading = false;
      return content;

    case types.CREATE_FOLDER_SUCCESS: {
      const folderItem = {};
      folderItem.id = action.folder.id;
      folderItem.title = action.folder.title;
      folderItem.new = true;
      content.data = [folderItem, ...state.data];
      return content;
    }

    case types.CREATE_FOLDER_V2_SUCCESS: {
      // Set new to false on all other folders before adding the new item
      const newData = content.data.map((item) => {
        if (item.type === `folder`) {
          return update(item, {
            new: { $set: false },
          });
        }
        return item;
      });

      // if the parent folder is shared - our new folder is too
      let accessors = [];
      let shared = false;
      if (action.parentId !== null) {
        const parentIdx = newData.findIndex(item => item.id === action.parentId);
        shared = newData[parentIdx].shared || false;
        accessors = newData[parentIdx].accessors || [];
      }

      const folderItem = {
        id: action.folderId,
        parent_id: action.parentId,
        title: action.folderName,
        new: true,
        type: `folder`,
        owned_by_me: true,
        accessors,
        shared,
      };
      // add new folder at start before organising folders
      const newFolderData = organiseFolderData([folderItem, ...newData]);
      content.data = newFolderData.data;
      content.idTree = newFolderData.idTree;
      return content;
    }

    case types.ADD_TO_FOLDER_SUCCESS:
      // We don't need to do anything now, but will when we have an API that shows which folders already contain the item
      return state;

    case types.EDIT_FOLDER_SUCCESS: {
      const folderId = Number(action.folderId);
      const newData = content.data.map((folder) => {
        if (folder.id === folderId) {
          return update(folder, {
            title: { $set: action.updates.title },
          });
        }
        return folder;
      });
      // need to re-organise as changing the title changes the order
      const newFolderData = organiseFolderData(newData);
      content.data = newFolderData.data;
      content.idTree = newFolderData.idTree;
      return content;
    }

    case types.MOVE_FOLDER_OPTIMISTIC: {
      let newData = [...content.data];

      const folderId = Number(action.folderId);
      const currentFolder = newData.find(folder => folder.type === `folder` && folder.id === folderId);

      let accessors;
      let shared;
      if (action.updates.parent_id === null) {
        // no change to shared state
        shared = currentFolder.shared;
        accessors = currentFolder.accessors || [];
      } else {
        const newParent = newData.find(folder => folder.type === `folder` && folder.id === action.updates.parent_id);
        // maintain share state OR become shared if parent is
        shared = currentFolder.shared || newParent.shared;
        // maintain current accessors and adopt any from the parent folder
        const currentAccessors = currentFolder.accessors || [];
        const newAccessors = newParent.accessors || [];
        accessors = [...currentAccessors, ...newAccessors];
      }

      // update the relevant folders
      const folderIDsToUpdate = content.idTree[folderId];
      let parentId;
      newData = newData.map((item) => {
        if (item.type === `folder` && folderIDsToUpdate.has(item.id)) {
          if (item.id === folderId) {
            // this is the folder moved
            parentId = action.updates.parent_id;
          } else {
            parentId = item.parent_id;
          }
          return update(item, {
            parent_id: { $set: parentId },
            previousState: { $set: {
              accessors: item.accessors,
              parent_id: item.parent_id,
              shared: item.shared,
            } },
            shared: { $set: shared },
            accessors: { $set: accessors },
          });
        }
        return item;
      });

      // reorganise folder data
      const newFolderData = organiseFolderData(newData);
      content.data = newFolderData.data;
      content.idTree = newFolderData.idTree;
      return content;
    }

    case types.MOVE_FOLDER_REVERT: {
      let newData = [...content.data];
      const folderId = Number(action.folderId);

      // // revert the state back to what it was previously on the relevant folders
      const folderIDsToUpdate = content.idTree[folderId];
      newData = newData.map((item) => {
        if (item.type === `folder` && folderIDsToUpdate.has(item.id)) {
          return update(item, {
            parent_id: { $set: item.previousState.parent_id },
            previousState: { $set: undefined },
            shared: { $set: item.previousState.shared },
            accessors: { $set: item.previousState.accessors },
          });
        }
        return item;
      });

      // un-reorganise folder data
      const newFolderData = organiseFolderData(newData);
      content.data = newFolderData.data;
      content.idTree = newFolderData.idTree;
      return content;
    }

    case types.REPLACE_FOLDER_OPTIMISTIC: {
      const folderId = Number(action.folderMovedId);

      // update the moved folder and delete the replaced folder
      content.data = content.data.reduce((data, item) => {
        if (item.type === `folder`) {
          if (item.id === folderId) {
            // this is the folder moved
            data.push(update(item, {
              parent_id: { $set: action.folderConflict.parent_id },
              previousState: { $set: { parent_id: item.parent_id } },
            }));
          } else if (item.id !== action.folderConflict.id) {
            // keep everything except the folder replaced
            data.push(item);
          }
        } else {
          // keep all content
          data.push(item);
        }
        return data;
      }, []);

      return content;
    }

    case types.REPLACE_FOLDER_COMPLETION: {
      // tidy up - delete folders and content within the replaced folder
      // check IDs of kept folders for when folder moved is child of folder replaced
      const folderIdsDeleted = content.idTree[action.folderConflict.id];
      const folderIdsKept = content.idTree[action.folderMovedId];
      const newData = content.data.filter((item) => {
        if (item.type === `folder`) {
          return !folderIdsDeleted.has(item.id) || folderIdsKept.has(item.id);
        }
        return !folderIdsDeleted.has(item.parent_id) || folderIdsKept.has(item.parent_id);
      });

      // re-organise folder data
      const newFolderData = organiseFolderData(newData);
      content.data = newFolderData.data;
      content.idTree = newFolderData.idTree;
      return content;
    }

    case types.REPLACE_FOLDER_REVERT: {
      const folderId = Number(action.folderMovedId);

      // revert the moved folder to original location
      const newData = content.data.map((item) => {
        if (item.type === `folder` && item.id === folderId) {
          return update(item, {
            parent_id: { $set: item.previousState.parent_id },
            previousState: { $set: undefined },
          });
        }
        return item;
      });

      // reinstate the replaced folder
      const revertedData = [action.folderConflict, ...newData];

      // un-reorganise folder data
      const newFolderData = organiseFolderData(revertedData);
      content.data = newFolderData.data;
      content.idTree = newFolderData.idTree;
      return content;
    }

    case types.DELETE_FOLDER_SUCCESS: {
      const folderId = Number(action.folderId);
      const newData = [...content.data];

      // remove the folder branch from the data
      const childFolders = content.idTree[folderId];
      content.data = newData.filter((item) => {
        if (item.type === `folder`) {
          return !childFolders.has(item.id);
        }
        return !childFolders.has(item.parent_id);
      });

      // remove reference to folder in ID tree
      const newIdTree = {};
      for (const [parentId, childFolderIds] of Object.entries(content.idTree)) {
        if (parentId !== action.folderId) {
          childFolderIds.delete(folderId);
          newIdTree[Number(parentId)] = childFolderIds;
        }
      }
      content.idTree = newIdTree;
      return content;
    }

    case types.SHARE_FOLDER_SUCCESS: {
      const sharedFolders = content.idTree[action.folderId];
      let accessors;

      content.data = content.data.map((item) => {
        if (item.type === `folder` && sharedFolders.has(item.id)) {
          const updateData = { shared: { $set: true } };

          if (item.info === `full`) {
            accessors = [...item.accessors, action.accessor];
            updateData.accessors = { $set: accessors };
          }
          return update(item, updateData);
        }
        return item;
      });
      return content;
    }

    case types.REMOVE_FOLDER_SHARE_SUCCESS: {
      let accessors;
      content.data = content.data.map((item) => {
        if (item.type === `folder` && action.data.folder_ids.includes(item.id)) {
          const updateData = { shared: { $set: !action.data.orphan_folder_ids.includes(item.id) } };

          if (item.info === `full`) {
            accessors = item.accessors.filter(accessor => accessor.id !== action.accessor.id);
            updateData.accessors = { $set: accessors };
          }
          return update(item, updateData);
        }
        return item;
      });
      return content;
    }

    case types.CHANGE_ITEM_FOLDER_OPTIMISTIC: {
      const newData = [...content.data];
      const itemIndex = newData.findIndex(item => item.id === Number(action.folderContentId));
      // keep the previous location in case we need to revert
      const previousParent = newData[itemIndex].parent_id;
      // update the folder id
      newData[itemIndex] = update(newData[itemIndex], {
        previousParent: { $set: previousParent },
        parent_id: { $set: Number(action.newFolder) },
      });
      content.data = newData;
      return content;
    }

    case types.CHANGE_ITEM_FOLDER_REVERT: {
      const newData = [...content.data];
      const itemIndex = newData.findIndex(item => item.id === Number(action.folderContentId));
      newData[itemIndex] = update(newData[itemIndex], {
        parent_id: { $set: newData[itemIndex].previousParent },
        previousParent: { $set: undefined },
      });

      content.data = newData;
      return content;
    }

    case types.DELETE_ITEM_FROM_FOLDER_SUCCESS: {
      const itemIndex = content.data.findIndex(item => item.id === Number(action.folderContentId));
      const newData = [...content.data];

      // remove the item
      newData.splice(itemIndex, 1);
      content.data = newData;
      return content;
    }

    case types.LOAD_FOLDER_CONTENT_SUCCESS: {
      content.data = content.data.map((item) => {
        if (item.type === action.data.type && item.content_id === action.data.content_id) {
          return update(item, {
            has_video: { $set: action.data.has_video },
            is_new: { $set: action.data.is_new },
            plaintext_summary: { $set: action.data.plaintext_summary },
            stream_image_url: { $set: action.data.stream_image_url },
            tcm_age_range: { $set: action.data.tcm_age_range },
            thumbnail_image_url: { $set: action.data.thumbnail_image_url },
            usa_age_range: { $set: action.data.usa_age_range },
            info: { $set: action.data.info },
          });
        }
        return item;
      });
      return content;
    }

    case types.LOAD_AVAILABLE_ACCESSORS_SUCCESS: {
      const availableAccessors = action.data || [];
      content.availableAccessors = availableAccessors.sort(sortByName);
      return content;
    }

    case types.LOAD_CURRENT_FOLDER_ACCESSORS_SUCCESS: {
      content.data = content.data.map((item) => {
        if (item.type === `folder` && item.id === action.folderId) {
          return update(item, {
            accessors: { $set: action.data },
            shared: { $set: action.data.length > 0 },
          });
        }
        return item;
      });
      return content;
    }

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./library/app/reducers/folderReducer.js